import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Lock, Eye, EyeOff, LogIn, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const UserLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [formData, setFormData] = useState({
    identifier: "", // Email or username
    password: ""
  });

  // Load saved credentials on mount
  useEffect(() => {
    const savedIdentifier = localStorage.getItem("user_remembered_identifier");
    const savedPassword = localStorage.getItem("user_remembered_password");
    if (savedIdentifier && savedPassword) {
      setFormData({ identifier: savedIdentifier, password: savedPassword });
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier.trim() || !formData.password) {
      setError("Please enter your email/username and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/users/login`, {
        identifier: formData.identifier.trim(),
        password: formData.password
      });

      // Store token and user info
      localStorage.setItem("userToken", res.data.token);
      localStorage.setItem("userData", JSON.stringify(res.data.user));
      
      // Handle remember me
      if (rememberMe) {
        localStorage.setItem("user_remembered_identifier", formData.identifier.trim());
        localStorage.setItem("user_remembered_password", formData.password);
      } else {
        localStorage.removeItem("user_remembered_identifier");
        localStorage.removeItem("user_remembered_password");
      }
      
      // Redirect to home or previous page
      const returnUrl = new URLSearchParams(window.location.search).get("return") || "/";
      navigate(returnUrl);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505]">
      
      
      
      <div className="pt-8 pb-16 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 p-8"
          >
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">
                Welcome Back
              </h1>
              <p className="text-zinc-500 text-sm mt-2">
                Sign in to your account
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Email or Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    name="identifier"
                    value={formData.identifier}
                    onChange={handleChange}
                    placeholder="Enter email or username"
                    className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-white/30"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="w-full bg-zinc-800 border border-white/10 text-white pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-white/30"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="sr-only"
                      data-testid="user-remember-me"
                    />
                    <div className={`w-5 h-5 border ${rememberMe ? 'bg-orange-500 border-orange-500' : 'bg-zinc-800 border-white/20'} flex items-center justify-center transition-colors`}>
                      {rememberMe && (
                        <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-zinc-400 hover:text-white">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-sm py-3 h-auto rounded-none mt-4"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            {/* Signup Link */}
            <p className="text-center text-zinc-500 text-sm mt-6">
              Don't have an account?{" "}
              <Link to="/signup" className="text-white hover:underline">
                Create Account
              </Link>
            </p>
          </motion.div>
        </div>
      </div>

      
    </main>
  );
};

export default UserLoginPage;
